import { useCallback, useMemo } from 'react';
import { createTabScopedStore } from './createTabScopedStore';

export type RequestHttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
export type RequestStatusBucket = '2xx' | '3xx' | '4xx' | '5xx';
export type RequestMimeType =
  | 'html'
  | 'json'
  | 'script'
  | 'css'
  | 'image'
  | 'other';

export type MethodFilter = RequestHttpMethod[];
export type StatusFilter = RequestStatusBucket[];
export type MimeTypeFilter = RequestMimeType[];
export type SortField =
  | 'sequentialId'
  | 'timestamp'
  | 'status'
  | 'method'
  | 'url'
  | 'host'
  | 'length';
export type SortOrder = 'asc' | 'desc';

const REQUESTS_SORT_FIELDS = [
  'sequentialId',
  'timestamp',
  'status',
  'method',
  'url',
  'host',
  'length',
] as const satisfies readonly SortField[];

export function isRequestsSortField(value: string): value is SortField {
  return (REQUESTS_SORT_FIELDS as readonly string[]).includes(value);
}

export type ExtensionFilterMode = 'hide' | 'show_only';
export type FilterMode = 'simple' | 'advanced';
export type SourceFilterValue = 'proxy' | 'copilot' | 'neo' | 'replay';
export type SourceFilter = SourceFilterValue[];

type SimpleRequestsFilters = {
  searchTerm: string;
  methodFilter: MethodFilter;
  statusFilter: StatusFilter;
  mimeTypeFilter: MimeTypeFilter;
  selectedExtensions: string[];
};

type SimpleRequestsFilterState = SimpleRequestsFilters & {
  extensionFilterMode: ExtensionFilterMode;
};

type AdvancedRequestsFilters = {
  tqlQuery: string;
};

type RequestFilterActivityState = SimpleRequestsFilters &
  AdvancedRequestsFilters & {
    filterMode: FilterMode;
  };

// Common file extensions
export const COMMON_STATIC_EXTENSIONS = [
  '.js',
  '.css',
  '.woff',
  '.woff2',
  '.ttf',
  '.eot',
  '.otf',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.svg',
  '.ico',
  '.webp',
  '.map',
] as const;

// MIME type patterns for filtering
export const MIME_TYPE_PATTERNS = {
  html: ['text/html'],
  json: ['application/json', 'text/json'],
  script: [
    'application/javascript',
    'text/javascript',
    'application/x-javascript',
  ],
  css: ['text/css'],
  image: ['image/'],
  xml: ['application/xml', 'text/xml'],
} as const;

type RequestsFilterValues = {
  // Basic filters
  searchTerm: string;
  methodFilter: MethodFilter;
  statusFilter: StatusFilter;

  // Sort
  sortField: SortField;
  sortOrder: SortOrder;

  // MIME type filter
  mimeTypeFilter: MimeTypeFilter;

  // Extension filter
  extensionFilterMode: ExtensionFilterMode;
  selectedExtensions: string[];

  // Advanced TQL filter
  filterMode: FilterMode;
  tqlQuery: string;
  sourceFilter: SourceFilter;

  // UI state
  showAdvancedFilters: boolean;
};

type RequestsFilterActions = {
  // Actions
  setSearchTerm: (term: string) => void;
  setMethodFilter: (method: MethodFilter) => void;
  setStatusFilter: (status: StatusFilter) => void;
  setSortField: (field: SortField) => void;
  setSortOrder: (order: SortOrder) => void;
  setMimeTypeFilter: (mimeType: MimeTypeFilter) => void;
  setExtensionFilterMode: (mode: ExtensionFilterMode) => void;
  setSelectedExtensions: (extensions: string[]) => void;
  toggleSelectedExtension: (extension: string) => void;
  selectAllStaticExtensions: () => void;
  setShowAdvancedFilters: (show: boolean) => void;
  setFilterMode: (mode: FilterMode) => void;
  setTqlQuery: (query: string) => void;
  setSourceFilter: (source: SourceFilter) => void;
  resetSimpleFilters: () => void;
  resetAdvancedFilters: () => void;
  resetFilters: () => void;
};

type RequestsFilterState = RequestsFilterValues & RequestsFilterActions;

const initialSimpleFilters: SimpleRequestsFilterState = {
  searchTerm: '',
  methodFilter: [],
  statusFilter: [],
  mimeTypeFilter: [],
  extensionFilterMode: 'hide',
  selectedExtensions: [],
};

const initialAdvancedFilters: AdvancedRequestsFilters = {
  tqlQuery: '',
};

const initialState: RequestsFilterValues = {
  ...initialSimpleFilters,
  ...initialAdvancedFilters,
  sortField: 'timestamp',
  sortOrder: 'desc',
  filterMode: 'simple',
  sourceFilter: ['proxy', 'copilot'],
  showAdvancedFilters: false,
};

const STATUS_FILTER_VALUES: RequestStatusBucket[] = [
  '2xx',
  '3xx',
  '4xx',
  '5xx',
];

const METHOD_FILTER_VALUES: RequestHttpMethod[] = [
  'GET',
  'POST',
  'PUT',
  'DELETE',
  'PATCH',
];

const MIME_FILTER_VALUES: RequestMimeType[] = [
  'html',
  'json',
  'script',
  'css',
  'image',
  'other',
];

const SOURCE_FILTER_VALUES: SourceFilterValue[] = [
  'proxy',
  'copilot',
  'neo',
  'replay',
];

function coerceFilterArray<T extends string>({
  value,
  allowedValues,
}: {
  value: unknown;
  allowedValues: readonly T[];
}): T[] {
  const allowed = new Set<string>(allowedValues);

  if (Array.isArray(value)) {
    return value.filter(
      (item): item is T => typeof item === 'string' && allowed.has(item),
    );
  }

  if (typeof value === 'string') {
    if (value === 'ALL') {
      return [];
    }

    return allowed.has(value) ? [value as T] : [];
  }

  return [];
}

function coerceStatusFilter(value: unknown): StatusFilter {
  return coerceFilterArray({
    value,
    allowedValues: STATUS_FILTER_VALUES,
  });
}

function coerceMethodFilter(value: unknown): MethodFilter {
  return coerceFilterArray({
    value,
    allowedValues: METHOD_FILTER_VALUES,
  });
}

function coerceMimeTypeFilter(value: unknown): MimeTypeFilter {
  return coerceFilterArray({
    value,
    allowedValues: MIME_FILTER_VALUES,
  });
}

function coerceSourceFilter(value: unknown): SourceFilter {
  if (!Array.isArray(value)) {
    return SOURCE_FILTER_VALUES.includes(value as SourceFilterValue)
      ? [value as SourceFilterValue]
      : [];
  }

  return value.filter((item): item is SourceFilterValue =>
    SOURCE_FILTER_VALUES.includes(item as SourceFilterValue),
  );
}

const useTabScopedRequestsFiltersBase =
  createTabScopedStore<RequestsFilterValues>(initialState);

export function useTabScopedRequestsFilters(): RequestsFilterState {
  const { filters, update } = useTabScopedRequestsFiltersBase();

  const statusFilter = useMemo(
    () => coerceStatusFilter(filters.statusFilter),
    [filters.statusFilter],
  );
  const methodFilter = useMemo(
    () => coerceMethodFilter(filters.methodFilter),
    [filters.methodFilter],
  );
  const mimeTypeFilter = useMemo(
    () => coerceMimeTypeFilter(filters.mimeTypeFilter),
    [filters.mimeTypeFilter],
  );
  const sourceFilter = useMemo(
    () => coerceSourceFilter(filters.sourceFilter),
    [filters.sourceFilter],
  );

  const setSearchTerm = useCallback(
    (term: string) => update({ searchTerm: term }),
    [update],
  );
  const setMethodFilter = useCallback(
    (method: MethodFilter) => update({ methodFilter: method }),
    [update],
  );
  const setStatusFilter = useCallback(
    (status: StatusFilter) => update({ statusFilter: status }),
    [update],
  );
  const setSortField = useCallback(
    (field: SortField) => update({ sortField: field }),
    [update],
  );
  const setSortOrder = useCallback(
    (order: SortOrder) => update({ sortOrder: order }),
    [update],
  );
  const setMimeTypeFilter = useCallback(
    (mimeType: MimeTypeFilter) => update({ mimeTypeFilter: mimeType }),
    [update],
  );
  const setExtensionFilterMode = useCallback(
    (mode: ExtensionFilterMode) => update({ extensionFilterMode: mode }),
    [update],
  );
  const setSelectedExtensions = useCallback(
    (extensions: string[]) => update({ selectedExtensions: extensions }),
    [update],
  );
  const setShowAdvancedFilters = useCallback(
    (show: boolean) => update({ showAdvancedFilters: show }),
    [update],
  );
  const setFilterMode = useCallback(
    (mode: FilterMode) => update({ filterMode: mode }),
    [update],
  );
  const setTqlQuery = useCallback(
    (query: string) => update({ tqlQuery: query }),
    [update],
  );
  const setSourceFilter = useCallback(
    (source: SourceFilter) => update({ sourceFilter: source }),
    [update],
  );
  const resetSimpleFilters = useCallback(
    () => update(initialSimpleFilters),
    [update],
  );
  const resetAdvancedFilters = useCallback(
    () => update(initialAdvancedFilters),
    [update],
  );
  const resetFilters = useCallback(
    () =>
      update({
        ...initialState,
        showAdvancedFilters: filters.showAdvancedFilters,
        filterMode: filters.filterMode,
      }),
    [filters.filterMode, filters.showAdvancedFilters, update],
  );
  const toggleSelectedExtension = useCallback(
    (extension: string) => {
      const normalized = extension.startsWith('.')
        ? extension
        : `.${extension}`;
      const current = filters.selectedExtensions;

      if (current.includes(normalized)) {
        update({
          selectedExtensions: current.filter((ext) => ext !== normalized),
        });

        return;
      }

      update({ selectedExtensions: [...current, normalized] });
    },
    [filters.selectedExtensions, update],
  );
  const selectAllStaticExtensions = useCallback(
    () => update({ selectedExtensions: [...COMMON_STATIC_EXTENSIONS] }),
    [update],
  );

  return {
    ...filters,
    statusFilter,
    methodFilter,
    mimeTypeFilter,
    sourceFilter,
    setSearchTerm,
    setMethodFilter,
    setStatusFilter,
    setSortField,
    setSortOrder,
    setMimeTypeFilter,
    setExtensionFilterMode,
    setSelectedExtensions,
    toggleSelectedExtension,
    selectAllStaticExtensions,
    setShowAdvancedFilters,
    setFilterMode,
    setTqlQuery,
    setSourceFilter,
    resetSimpleFilters,
    resetAdvancedFilters,
    resetFilters,
  };
}

export function hasActiveSimpleRequestsFilters({
  searchTerm,
  methodFilter,
  statusFilter,
  mimeTypeFilter,
  selectedExtensions,
}: SimpleRequestsFilters): boolean {
  return (
    searchTerm !== '' ||
    methodFilter.length > 0 ||
    statusFilter.length > 0 ||
    mimeTypeFilter.length > 0 ||
    selectedExtensions.length > 0
  );
}

export function hasActiveAdvancedRequestsFilters({
  tqlQuery,
}: AdvancedRequestsFilters): boolean {
  return tqlQuery.trim() !== '';
}

export function hasActiveRequestsFilters({
  searchTerm,
  methodFilter,
  statusFilter,
  mimeTypeFilter,
  selectedExtensions,
  tqlQuery,
  filterMode,
}: RequestFilterActivityState): boolean {
  if (filterMode === 'advanced') {
    return hasActiveAdvancedRequestsFilters({ tqlQuery });
  }

  return hasActiveSimpleRequestsFilters({
    searchTerm,
    methodFilter,
    statusFilter,
    mimeTypeFilter,
    selectedExtensions,
  });
}

// Helper to get URL extension
export function getUrlExtension(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    const lastDotIndex = pathname.lastIndexOf('.');

    if (lastDotIndex > 0 && lastDotIndex < pathname.length - 1) {
      return pathname.substring(lastDotIndex);
    }

    return null;
  } catch {
    return null;
  }
}

// Helper to check if URL matches extension filter
export function matchesExtensionFilter(
  url: string,
  selectedExtensions: string[],
  mode: ExtensionFilterMode,
): boolean {
  if (selectedExtensions.length === 0) return true;

  const extension = getUrlExtension(url);

  if (mode === 'hide') {
    // Hide mode: return false if extension matches (should be hidden)
    return !extension || !selectedExtensions.includes(extension);
  } else {
    // Show only mode: return true only if extension matches
    return extension !== null && selectedExtensions.includes(extension);
  }
}

// Helper to get MIME type from response headers
export function getMimeType(
  responseHeaders: Record<string, string | string[]> | undefined,
): string | null {
  if (!responseHeaders) return null;

  const contentType =
    responseHeaders['content-type'] || responseHeaders['Content-Type'];

  if (!contentType) return null;

  return (Array.isArray(contentType) ? contentType[0] : contentType)
    .split(';')[0]
    .trim()
    .toLowerCase();
}

// Helper to check if response matches MIME type filter
export function matchesMimeTypeFilter(
  responseHeaders: Record<string, string | string[]> | undefined,
  mimeTypeFilter: RequestMimeType,
): boolean {
  const mimeType = getMimeType(responseHeaders);

  if (!mimeType) return mimeTypeFilter === 'other';

  switch (mimeTypeFilter) {
    case 'html':
      return MIME_TYPE_PATTERNS.html.some((pattern) =>
        mimeType.includes(pattern),
      );
    case 'json':
      return MIME_TYPE_PATTERNS.json.some((pattern) =>
        mimeType.includes(pattern),
      );
    case 'script':
      return MIME_TYPE_PATTERNS.script.some((pattern) =>
        mimeType.includes(pattern),
      );
    case 'css':
      return MIME_TYPE_PATTERNS.css.some((pattern) =>
        mimeType.includes(pattern),
      );
    case 'image':
      return mimeType.startsWith('image/');
    case 'other':
      return (
        !MIME_TYPE_PATTERNS.html.some((p) => mimeType.includes(p)) &&
        !MIME_TYPE_PATTERNS.json.some((p) => mimeType.includes(p)) &&
        !MIME_TYPE_PATTERNS.script.some((p) => mimeType.includes(p)) &&
        !MIME_TYPE_PATTERNS.css.some((p) => mimeType.includes(p)) &&
        !mimeType.startsWith('image/')
      );
    default:
      return true;
  }
}

export function matchesMimeTypeFilters(
  responseHeaders: Record<string, string | string[]> | undefined,
  mimeTypeFilters: MimeTypeFilter,
): boolean {
  if (mimeTypeFilters.length === 0) {
    return true;
  }

  return mimeTypeFilters.some((mimeTypeFilter) =>
    matchesMimeTypeFilter(responseHeaders, mimeTypeFilter),
  );
}
